import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const BRAND_GREEN = "#648315";

export default function DashboardSignalRow({
  icon,
  label,
  value,
  color,
  onClick,
  tooltip,
  zeroText,
  priority = false,
}) {
  const numericValue = Number(value) || 0;
  const isActivePriority = priority && numericValue > 0;
  const showZeroText = numericValue === 0 && zeroText;

  const row = (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: onClick ? "pointer" : "default",
        gap: 1.25,
        px: 1.35,
        py: 1,
        borderRadius: "10px",
        backgroundColor: isActivePriority ? `${color}08` : "#ffffff",
        border: `1px solid ${isActivePriority ? `${color}55` : "rgba(0,51,108,0.1)"}`,
        boxShadow: isActivePriority ? `0 1px 5px ${color}14` : "0 1px 2px rgba(15,23,42,0.03)",
        transition: "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": onClick
          ? {
              backgroundColor: isActivePriority ? `${color}0f` : "#f8fbff",
              borderColor: isActivePriority ? `${color}88` : "rgba(0,51,108,0.2)",
              boxShadow: "0 4px 12px rgba(15,23,42,0.07)",
              transform: "translateY(-1px)",
              "& .signal-arrow": { opacity: 1, transform: "translateX(1px)" },
            }
          : undefined,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "10px",
            backgroundColor: isActivePriority ? `${color}20` : `${color}14`,
            border: `1px solid ${isActivePriority ? `${color}3d` : "transparent"}`,
            display: "grid",
            placeItems: "center",
            flex: "0 0 auto",
            color,
            "& svg": { fontSize: 26 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 800,
              color: "#173a63",
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
          {showZeroText && (
            <Typography
              sx={{
                color: "#6b7f9f",
                fontSize: 11.5,
                fontWeight: 600,
                lineHeight: 1.25,
                mt: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {zeroText}
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
        <Box
          className="signal-value"
          sx={{
            minWidth: 44,
            height: 32,
            px: 1.15,
            borderRadius: "999px",
            backgroundColor: isActivePriority ? `${color}14` : "rgba(100,131,21,0.08)",
            border: `1px solid ${isActivePriority ? `${color}45` : "rgba(100,131,21,0.18)"}`,
            display: "grid",
            placeItems: "center",
            transition: "background-color 0.15s ease",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: isActivePriority ? color : BRAND_GREEN, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
            {value}
          </Typography>
        </Box>
        {onClick && (
          <ArrowForwardIosIcon
            className="signal-arrow"
            sx={{
              fontSize: 10,
              color: "rgba(0,51,108,0.3)",
              opacity: 0.7,
              transition: "all 0.15s ease",
            }}
          />
        )}
      </Stack>
    </Box>
  );

  if (!tooltip) return row;
  return <Tooltip title={tooltip} arrow placement="top">{row}</Tooltip>;
}
